import { ContentBuilder } from "@components/ContentBuilder";
import { Layout } from "@components/Layout";
import VacatureDetailAboutBlock from "@components/VacatureDetail/VacatureDetailAboutBlock/VacatureDetailAboutBlock";
import VacatureDetailBenefitsBlock from "@components/VacatureDetail/VacatureDetailBenefitsBlock/VacatureDetailBenefitsBlock";
import VacatureDetailContactBlock from "@components/VacatureDetail/VacatureDetailContactBlock/VacatureDetailContactBlock";
import VacatureDetailDescriptionBlock from "@components/VacatureDetail/VacatureDetailDescriptionBlock/VacatureDetailDescriptionBlock";
import VacatureDetailHero, { getHeroImageForCluster } from "@components/VacatureDetail/VacatureDetailHero/VacatureDetailHero";
import DOMPurify from "isomorphic-dompurify";
import { GetServerSidePropsContext } from "next";
import Error from "next/error";
import { useRouter } from "next/router";
import { APITypes } from "utils/api.types";
import { fetchPageBySlug, fetchPositionBySlug } from "utils/fetchingFunctions";
import { getShareURLFr } from "utils/sharing";
export interface VacatureDetailProps {
  navigationBar: APITypes.Page["navigationBar"];
  block: APITypes.Components.ContentBlocks.VacatureDetail;
  position: APITypes.Components.Job;
  slug: string;
  localizations: APITypes.Localization[];
  errorCode: any;
  footer: APITypes.Page["footer"];
  isScriptLoaded: boolean;
  movingBar: APITypes.Page["movingBar"];
  sanitizedJobDescription: string;
  localeForSharingUrl: string;
}
const VacatureDetail = ({
  navigationBar,
  block,
  position,
  errorCode,
  slug,
  localizations,
  footer,
  isScriptLoaded,
  movingBar,
  sanitizedJobDescription
}: VacatureDetailProps) => {
  const router = useRouter();
  if (errorCode) {
    return <Error statusCode={errorCode} />;
  }
  return <Layout meta={{
    metaTitle: position.title,
    metaImage: getHeroImageForCluster(position.cluster, block),
    metaDescription: sanitizedJobDescription,
    metaUrl: getShareURLFr(router.asPath)
  }} data-sentry-element="Layout" data-sentry-component="VacatureDetail" data-sentry-source-file="[...slug].tsx">
      {/*NavBar*/}
      {navigationBar && <ContentBuilder contentBlocks={navigationBar.content} slug={slug} localizations={localizations} />}

      <VacatureDetailHero block={block} position={position} data-sentry-element="VacatureDetailHero" data-sentry-source-file="[...slug].tsx" />
      <VacatureDetailBenefitsBlock block={block} position={position} data-sentry-element="VacatureDetailBenefitsBlock" data-sentry-source-file="[...slug].tsx" />
      <VacatureDetailDescriptionBlock block={block} position={position} data-sentry-element="VacatureDetailDescriptionBlock" data-sentry-source-file="[...slug].tsx" />
      <VacatureDetailAboutBlock block={block} position={position} data-sentry-element="VacatureDetailAboutBlock" data-sentry-source-file="[...slug].tsx" />
      <VacatureDetailContactBlock vacatureForm={block.vacatureForm} position={position} isScriptLoaded={isScriptLoaded} data-sentry-element="VacatureDetailContactBlock" data-sentry-source-file="[...slug].tsx" />
      {/*Footer*/}
      {footer && <ContentBuilder contentBlocks={footer.content} slug={slug} localizations={localizations} isMovingBarPresent={movingBar != null} />}
      {/*Moving Bar*/}
      {movingBar && <ContentBuilder contentBlocks={movingBar.content} slug={slug} localizations={localizations} />}
    </Layout>;
};
export default VacatureDetail;
export async function getServerSideProps({
  locale,
  defaultLocale = "nl",
  params,
  res
}: GetServerSidePropsContext) {
  try {
    const env = process.env.NEXT_PUBLIC_NODE_ENV || process.env.NODE_ENV;
    // Cache policy:
    // Requests are cached for two days (max-age & s-maxage).
    // Server can reuse an existing cache for a day after it is stale, provided the request is revalidated in the background (state-while-revalidate)
    if (env === "production" || env === "prod") {
      res.setHeader("Cache-Control", "public, max-age=172800 s-maxage=172800, stale-while-revalidate=86400");
    }
    if (params && params.slug && params.slug[0]) {
      if (params.slug[0] === "detail") {
        throw new Error({
          title: "Not redirecting to the detail page.",
          statusCode: 404
        });
      }
      const [jobRequestResult, pageRequestResult] = await Promise.all([fetchPositionBySlug(locale, params.slug[0]), fetchPageBySlug(`offres-demploi/detail`, locale ?? defaultLocale)]);
      let errorCode = null;
      if (jobRequestResult.errorCode) errorCode = jobRequestResult.errorCode;else if (pageRequestResult.errorCode) errorCode = pageRequestResult.errorCode;
      if (errorCode) {
        return {
          props: {
            errorCode: errorCode ?? null
          }
        };
      }
      if (jobRequestResult && jobRequestResult.data && jobRequestResult.data[0] && pageRequestResult && pageRequestResult.data && pageRequestResult.data[0]) {
        const job = jobRequestResult.data[0] as APITypes.Components.Job;
        const sanitizedJobDescription = DOMPurify.sanitize(job.jobDescription, {
          ALLOWED_TAGS: []
        });
        return {
          props: {
            errorCode: errorCode ?? null,
            navigationBar: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].navigationBar : null,
            slug: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].slug : null,
            localizations: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].localizations : null,
            block: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].content[0] : null,
            position: job,
            footer: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].footer : null,
            movingBar: pageRequestResult && pageRequestResult.data ? pageRequestResult.data[0].movingBar : null,
            sanitizedJobDescription
          }
        };
      } else {
        console.log("Error: this page does not exist");
        throw new Error({
          title: "This does not exist",
          statusCode: 404
        });
      }
    }
    return {
      notFound: true
    };
  } catch (error) {
    console.error(error);
    return {
      redirect: {
        destination: `/offres-demploi`,
        statusCode: 307
      }
    };
  }
}